import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { projects } from '@/data/mock';
import { cn } from '@/lib/utils';

export const ProjectsSection = ({ showCarousel = true, limit = 6, className }) => {
  const displayProjects = limit ? projects.slice(0, limit) : projects;

  const ProjectCard = ({ project, index }) => (
    <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 h-full">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent" />
        <Badge
          className={cn(
            'absolute top-4 left-4',
            project.status === 'Completed'
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-amber-600 hover:bg-amber-700'
          )}
        >
          {project.status}
        </Badge>
        <Badge variant="secondary" className="absolute top-4 right-4 bg-white/90 text-gray-700">
          {project.category}
        </Badge>
      </div>
      <CardContent className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-red-600 transition-colors line-clamp-2">
          {project.title}
        </h3>
        <p className="text-gray-600 leading-relaxed line-clamp-3">
          {project.description}
        </p>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="inline-flex items-center gap-2 text-red-600 font-medium text-sm group-hover:gap-3 transition-all">
            Learn More
            <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <section className={cn('py-20 bg-white', className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
          <div>
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Featured Work</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900">Our Projects</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-xl">
              Delivering landmark infrastructure projects that shape communities and advance mobility.
            </p>
          </div>
          <Link
            to="/projects"
            className="mt-6 md:mt-0 inline-flex items-center gap-2 text-red-600 font-semibold hover:gap-4 transition-all duration-300"
          >
            View All Projects
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Projects Display */}
        {showCarousel ? (
          <div className="px-12">
            <Carousel
              opts={{
                align: 'start',
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {displayProjects.map((project, index) => (
                  <CarouselItem key={project.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                    <ProjectCard project={project} index={index} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="-left-6 bg-white shadow-lg border-gray-200 hover:bg-red-50 hover:border-red-200" />
              <CarouselNext className="-right-6 bg-white shadow-lg border-gray-200 hover:bg-red-50 hover:border-red-200" />
            </Carousel>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayProjects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectsSection;
